import React, { useContext, useEffect, useState } from "react";
import { Spinner, Alert } from "react-bootstrap";
import IntegrationConfigForm from "./IntegrationConfigForm";
import IntegrationTestAuthPanel from "./IntegrationTestAuthPanel";
import SidebarContext from "../../components/Sidebar/SidebarContext";
import { useSyncedField } from "../../components/Sidebar";
import { getAvailableAuthSchemes } from "../../services/RepositoryService";

/**
 * Second tab of the setup wizard — everything needed to prove the
 * integration can authenticate, isolated from the actual data-connection
 * details (which live on the Connection tab).
 *
 * Auth is fully decoupled from the integration template: every scheme in the
 * auth_schemes.available catalog is selectable here, not just whichever one
 * the template suggests as a default — picking a different scheme just
 * switches which fields render, namespaced under
 * config.values.auth[schemeKey] so switching back and forth never loses
 * what you typed for another scheme (see IntegrationConfigForm's authScheme prop).
 *
 * "Test Auth" can only verify what's testable in isolation: for OAuth2
 * Client Credentials it makes a real token-endpoint call; for every other
 * scheme (no separate auth-only endpoint exists) it just confirms required
 * fields are filled — real verification happens once Test Connection fires
 * for real, which always re-runs this same auth check first (server-side,
 * see IntegrationService.testAuthentication).
 */
const IntegrationAuthPage = ({ template }) => {
    const { entity, emittedData } = useContext(SidebarContext);
    const [config, setConfig] = useSyncedField("config", { values: {} });
    const [schemes, setSchemes] = useState(null);

    useEffect(() => {
        getAvailableAuthSchemes().then((data) => setSchemes(data || []));
    }, []);

    const defaultSchemeKey = template?.auth?.scheme || "no_auth";
    const selectedSchemeKey = config?.values?.auth_scheme || defaultSchemeKey;

    const selectScheme = (key) => {
        setConfig({
            ...config,
            values: { ...(config?.values ?? {}), auth_scheme: key },
        });
    };

    if (schemes === null) {
        return (
            <div className="p-3 text-center">
                <Spinner animation="border" size="sm" />
            </div>
        );
    }

    const selectedScheme = schemes.find((s) => s.key === selectedSchemeKey);
    const hasOwnProxyField = (selectedScheme?.fields || []).some((f) => f.key === "auth_proxy_url");
    // allowProbe is optional, defaults to true — distinct from `supported`:
    // an unsupported scheme always shows the disabled "Connect" stub
    // regardless of allowProbe; a supported scheme with allowProbe === false
    // hides the Test Auth action entirely rather than offering a probe.
    const showProbe = !selectedScheme || selectedScheme.supported === false || selectedScheme.allowProbe !== false;

    return (
        <div>
            <div className="px-3 pt-3">
                <div className="d-flex flex-wrap gap-2 mb-3">
                    {schemes.map((scheme) => (
                        <button
                            key={scheme.key}
                            type="button"
                            className={`btn btn-sm ${
                                selectedSchemeKey === scheme.key ? "btn-primary" : "btn-outline-secondary"
                            }`}
                            onClick={() => selectScheme(scheme.key)}
                            title={scheme.description}
                        >
                            {scheme.name}
                        </button>
                    ))}
                </div>

                {selectedScheme && !hasOwnProxyField && (
                    <Alert variant="info" className="py-2 px-3 small mb-3">
                        <i className="fa fa-info-circle me-2" />
                        This authentication method doesn't make a separate network call, so it
                        doesn't need its own proxy. If you're behind a corporate proxy, set the{" "}
                        <strong>Outbound Proxy URL</strong> field on the <strong>Connection</strong>{" "}
                        tab instead — it covers this request too.
                    </Alert>
                )}
            </div>

            {selectedScheme && (selectedScheme.fields || []).length > 0 && (
                <IntegrationConfigForm schema={selectedScheme.fields} authScheme={selectedScheme.key} />
            )}

            {showProbe && (
                <div className="px-3 pb-3">
                    <IntegrationTestAuthPanel
                        testPayload={{ id: entity?.id, ...emittedData }}
                        schemeName={selectedScheme?.name}
                        unsupported={selectedScheme ? !selectedScheme.supported : false}
                    />
                </div>
            )}
        </div>
    );
};

export default IntegrationAuthPage;
